export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  SCORER: 'scorer',
  GUEST: 'guest',
};

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SCORER]: 'Scorer',
  [ROLES.GUEST]: 'Guest',
};

/** Mirrors backend ROLE_CREATION_RULES — who may create which roles. */
export const ROLE_CREATION_RULES = {
  [ROLES.SUPER_ADMIN]: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SCORER],
  [ROLES.ADMIN]: [ROLES.SCORER],
};

export function getCreatableRoles(actorRole) {
  const allowed = ROLE_CREATION_RULES[actorRole] || [];
  return allowed.map((value) => ({ value, label: ROLE_LABELS[value] || value }));
}

export const API_ENDPOINTS = {
  HEALTH: '/health',
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    PROFILE: '/auth/me',
    PREFERENCES: '/auth/preferences',
    CHANGE_PASSWORD: '/auth/change-password',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CREATE_USER: '/auth/users',
    UPLOAD_AVATAR: '/auth/upload-avatar',
    LOGIN_HISTORY: '/auth/login-history',
    SESSIONS: '/auth/sessions',
    LOGOUT_ALL: '/auth/logout-all',
    SECURITY: '/auth/security',
  },
  USERS: {
    BASE: '/users',
    UPLOAD: '/users/upload',
    DASHBOARD_STATS: '/users/dashboard-stats',
    BY_ID: (id) => `/users/${id}`,
    STATUS: (id) => `/users/${id}/status`,
    ASSIGNED_MATCHES: (id) => `/users/${id}/assigned-matches`,
  },
  ROLES: {
    BASE: '/roles',
    BY_ID: (id) => `/roles/${id}`,
  },
  TOURNAMENTS: {
    BASE: '/tournaments',
    RULE_TEMPLATES: '/tournaments/rule-templates',
    UPLOAD: '/tournaments/upload',
    BY_ID: (id) => `/tournaments/${id}`,
    STATISTICS: (id) => `/tournaments/${id}/statistics`,
    TEAMS: (id) => `/tournaments/${id}/teams`,
    GENERATE_FIXTURES: (id) => `/tournaments/${id}/generate-fixtures`,
    FIXTURES: (id) => `/tournaments/${id}/fixtures`,
    STANDINGS: (id) => `/tournaments/${id}/standings`,
    BRACKET: (id) => `/tournaments/${id}/bracket`,
    RECALCULATE_STANDINGS: (id) => `/tournaments/${id}/recalculate-standings`,
    REGENERATE_STATISTICS: (id) => `/tournaments/${id}/regenerate-statistics`,
    CORRECT_SCORE: (matchId) => `/tournaments/matches/${matchId}/correct-score`,
    GENERATE_SCHEDULE: (id) => `/tournaments/${id}/generate-schedule`,
    PUBLISH_SCHEDULE: (id) => `/tournaments/${id}/publish-schedule`,
    CALENDAR: (id) => `/tournaments/${id}/calendar`,
    TIMELINE: (id) => `/tournaments/${id}/timeline`,
    LIVE_OPERATIONS: (id) => `/tournaments/${id}/live-operations`,
    SEEDING_PREVIEW: (id) => `/tournaments/${id}/seeding/preview`,
    SEEDING_REGENERATE: (id) => `/tournaments/${id}/seeding/regenerate`,
    SEEDING_FINALIZE: (id) => `/tournaments/${id}/seeding/finalize`,
    REFEREE_WORKLOAD: (id) => `/tournaments/${id}/referee-workload`,
    ADVANCED_REPORTS: (id) => `/tournaments/${id}/advanced-reports`,
    REFEREES: '/tournaments/referees',
    ASSIGN_REFEREES: (matchId) => `/tournaments/matches/${matchId}/referees`,
  },
  PUBLIC_TOURNAMENTS: {
    BY_ID: (id) => `/public/tournaments/${id}`,
    FIXTURES: (id) => `/public/tournaments/${id}/fixtures`,
    STANDINGS: (id) => `/public/tournaments/${id}/standings`,
    BRACKET: (id) => `/public/tournaments/${id}/bracket`,
    STATISTICS: (id) => `/public/tournaments/${id}/statistics`,
    TEAMS: (id) => `/public/tournaments/${id}/teams`,
    CALENDAR: (id) => `/public/tournaments/${id}/calendar`,
    TIMELINE: (id) => `/public/tournaments/${id}/timeline`,
    LIVE_OPERATIONS: (id) => `/public/tournaments/${id}/live-operations`,
  },
  TEAMS: {
    BASE: '/teams',
    UPLOAD: '/teams/upload',
    EXPORT: '/teams/export',
    BULK_DELETE: '/teams/bulk-delete',
    BY_ID: (id) => `/teams/${id}`,
    PROFILE: (id) => `/teams/${id}/profile`,
    STATISTICS: (id) => `/teams/${id}/statistics`,
    GALLERY: (id) => `/teams/${id}/gallery`,
    PLAYERS: (id) => `/teams/${id}/players`,
    PLAYER: (teamId, playerId) => `/teams/${teamId}/players/${playerId}`,
    GALLERY_IMAGE: (teamId, imageId) => `/teams/${teamId}/gallery/${imageId}`,
  },
  PLAYERS: {
    BASE: '/players',
    UPLOAD: '/players/upload',
    EXPORT: '/players/export',
    BULK_DELETE: '/players/bulk-delete',
    BY_ID: (id) => `/players/${id}`,
    PROFILE: (id) => `/players/${id}/profile`,
    STATISTICS: (id) => `/players/${id}/statistics`,
    GALLERY: (id) => `/players/${id}/gallery`,
  },
  MATCHES: {
    BASE: '/matches',
    BY_ID: (id) => `/matches/${id}`,
    START: (id) => `/matches/${id}/start`,
    END: (id) => `/matches/${id}/end`,
  },
  SCORING: {
    BY_MATCH: (matchId) => `/scoring/${matchId}`,
    POINT: (matchId) => `/scoring/${matchId}/point`,
    UNDO: (matchId) => `/scoring/${matchId}/undo`,
    TIMEOUT: (matchId) => `/scoring/${matchId}/timeout`,
    END_TIMEOUT: (matchId) => `/scoring/${matchId}/end-timeout`,
    END_SET: (matchId) => `/scoring/${matchId}/end-set`,
    END_MATCH: (matchId) => `/scoring/${matchId}/end-match`,
    SERVE: (matchId) => `/scoring/${matchId}/serve`,
    LINEUP: (matchId) => `/scoring/${matchId}/lineup`,
  },
  SPONSORS: {
    BASE: '/sponsors',
    ACTIVE: '/sponsors/active',
    SUMMARY: '/sponsors/summary',
    UPLOAD: '/sponsors/upload',
    EXPORT: '/sponsors/export',
    BULK_DELETE: '/sponsors/bulk-delete',
    BY_ID: (id) => `/sponsors/${id}`,
  },
  GROUNDS: {
    BASE: '/grounds',
    SUMMARY: '/grounds/summary',
    REPORTS: '/grounds/reports',
    UPLOAD: '/grounds/upload',
    EXPORT: '/grounds/export',
    BULK_DELETE: '/grounds/bulk-delete',
    BY_ID: (id) => `/grounds/${id}`,
    STATUS: (id) => `/grounds/${id}/status`,
    COURTS: (id) => `/grounds/${id}/courts`,
    COURT: (id, courtId) => `/grounds/${id}/courts/${courtId}`,
    AVAILABILITY: (id) => `/grounds/${id}/availability`,
    AVAILABLE_COURTS: (id) => `/grounds/${id}/available-courts`,
  },
  PUBLIC_GROUNDS: {
    BASE: '/public/grounds',
    BY_ID: (id) => `/public/grounds/${id}`,
  },
  PUBLIC_PORTAL: {
    HOME: '/public/portal/home',
    STATS: '/public/portal/stats',
    SEARCH: '/public/portal/search',
    TOURNAMENTS: '/public/portal/tournaments',
    TOURNAMENTS_LIST: '/public/tournaments',
    MATCHES: '/public/portal/matches',
    MATCH: (id) => `/public/portal/matches/${id}`,
    TEAMS: '/public/portal/teams',
    TEAM: (id) => `/public/portal/teams/${id}`,
    PLAYERS: '/public/portal/players',
    PLAYER: (id) => `/public/portal/players/${id}`,
    GALLERY: '/public/portal/gallery',
    ALBUMS: '/public/portal/gallery/albums',
    ALBUM: (id) => `/public/portal/gallery/albums/${id}`,
    SPONSORS: '/public/portal/sponsors',
  },
  ALBUMS: {
    BASE: '/albums',
    BY_ID: (id) => `/albums/${id}`,
  },
  MEDIA: {
    BASE: '/media',
    UPLOAD: '/media/upload',
    RECENT: '/media/recent',
    STATS: '/media/stats',
    EXPORT: '/media/export',
    BULK_DELETE: '/media/bulk-delete',
    BULK_MOVE: '/media/bulk-move',
    BY_ID: (id) => `/media/${id}`,
    BY_MATCH: (matchId) => `/media/match/${matchId}`,
  },
  NOTIFICATIONS: {
    BASE: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    READ_ALL: '/notifications/read-all',
    MARK_READ: (id) => `/notifications/${id}/read`,
    BY_ID: (id) => `/notifications/${id}`,
  },
  REPORTS: {
    DASHBOARD: '/reports/dashboard',
    MATCHES: '/reports/matches',
    TOURNAMENTS: '/reports/tournaments',
    TEAMS: '/reports/teams',
    PLAYERS: '/reports/players',
    GROUNDS: '/reports/grounds',
    SPONSORS: '/reports/sponsors',
    GALLERY: '/reports/gallery',
    NOTIFICATIONS: '/reports/notifications',
    SCORING: '/reports/scoring',
    LIVE: '/reports/live',
    PERFORMANCE: '/reports/performance',
    EXPORT_PDF: '/reports/export/pdf',
    EXPORT_EXCEL: '/reports/export/excel',
    EXPORT_CSV: '/reports/export/csv',
    SCHEDULED: '/reports/scheduled',
    SCHEDULED_BY_ID: (id) => `/reports/scheduled/${id}`,
  },
};

export const PUBLIC_ROUTES = {
  HOME: '/',
  ABOUT: '/about',
  TOURNAMENTS: '/tournaments',
  TOURNAMENT: '/tournament/:id',
  FIXTURES: '/fixtures',
  RESULTS: '/results',
  LIVE: '/live',
  TEAMS: '/teams',
  TEAM: '/team/:id',
  PLAYERS: '/players',
  PLAYER: '/player/:id',
  GROUNDS: '/grounds',
  GROUND: '/ground/:id',
  GALLERY: '/gallery',
  GALLERY_ALBUM: '/gallery/:albumId',
  SPONSORS: '/sponsors',
  CONTACT: '/contact',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  MATCH: '/match/:matchId',
  NOT_FOUND: '/404',
};

export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  TOURNAMENTS: '/tournaments',
  TOURNAMENT_CREATE: '/tournaments/new',
  TOURNAMENT_DETAILS: '/tournaments/:id',
  TOURNAMENT_EDIT: '/tournaments/:id/edit',
  TOURNAMENT_SETTINGS: '/tournaments/:id/settings',
  TEAMS: '/teams',
  TEAM_CREATE: '/teams/new',
  TEAM_DETAILS: '/teams/:id',
  TEAM_EDIT: '/teams/:id/edit',
  TEAM_STATISTICS: '/teams/:id/statistics',
  TEAM_GALLERY: '/teams/:id/gallery',
  PLAYERS: '/players',
  PLAYER_CREATE: '/players/new',
  PLAYER_DETAILS: '/players/:id',
  PLAYER_EDIT: '/players/:id/edit',
  PLAYER_STATISTICS: '/players/:id/statistics',
  MATCHES: '/matches',
  MATCH_CREATE: '/matches/new',
  MATCH_DETAILS: '/matches/:id',
  MATCH_EDIT: '/matches/:id/edit',
  GROUNDS: '/grounds',
  GROUND_CREATE: '/grounds/new',
  GROUND_DETAILS: '/grounds/:id',
  GROUND_EDIT: '/grounds/:id/edit',
  REPORTS: '/reports',
  GALLERY: '/gallery',
  GALLERY_ALBUMS: '/gallery/albums',
  GALLERY_UPLOAD: '/gallery/upload',
  GALLERY_ALBUM: '/gallery/:albumId',
  SPONSORS: '/sponsors',
  SPONSOR_CREATE: '/sponsors/new',
  SPONSOR_DETAILS: '/sponsors/:id',
  SPONSOR_EDIT: '/sponsors/:id/edit',
  SETTINGS: '/settings',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_ROLES: '/settings/roles',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_PREFERENCES: '/settings/preferences',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_SECURITY: '/settings/security',
  NOTIFICATIONS: '/notifications',
  LIVE_SCORE: '/live-score/:matchId',
  SCORER_PANEL: '/scorer/:matchId',
  LED_SCOREBOARD: '/led/:matchId',
  PUBLIC_MATCH: '/match/:matchId',
  PUBLIC_TOURNAMENT: '/tournament/:id',
  LOGIN: '/login',
  DESIGN_SYSTEM: '/design-system',
  NOT_FOUND: '*',
  PERMISSION_DENIED: '/permission-denied',
};

export const QUERY_KEYS = {
  ME: ['me'],
  TOURNAMENTS: ['tournaments'],
  TOURNAMENT: (id) => ['tournament', id],
  TOURNAMENT_STATS: (id) => ['tournament-stats', id],
  TOURNAMENT_TEAMS: (id) => ['tournament-teams', id],
  TOURNAMENT_FIXTURES: (id, status = 'all') => ['tournament-fixtures', id, status || 'all'],
  TOURNAMENT_STANDINGS: (id) => ['tournament-standings', id],
  TOURNAMENT_BRACKET: (id) => ['tournament-bracket', id],
  TOURNAMENT_CALENDAR: (id, params) => ['tournament-calendar', id, params],
  TOURNAMENT_TIMELINE: (id) => ['tournament-timeline', id],
  TOURNAMENT_LIVE_OPS: (id) => ['tournament-live-ops', id],
  TOURNAMENT_SEEDING: (id) => ['tournament-seeding', id],
  REFEREES: ['referees'],
  RULE_TEMPLATES: ['rule-templates'],
  TEAMS: ['teams'],
  TEAM: (id) => ['team', id],
  TEAM_PROFILE: (id) => ['team-profile', id],
  TEAM_STATS: (id) => ['team-stats', id],
  PLAYERS: ['players'],
  PLAYER: (id) => ['player', id],
  PLAYER_PROFILE: (id) => ['player-profile', id],
  PLAYER_STATS: (id) => ['player-stats', id],
  MATCHES: ['matches'],
  MATCH: (id) => ['match', id],
  SCORING: (matchId) => ['scoring', matchId],
  SPONSORS: ['sponsors'],
  SPONSOR: (id) => ['sponsor', id],
  ACTIVE_SPONSORS: ['active-sponsors'],
  SPONSOR_SUMMARY: ['sponsor-summary'],
  GROUNDS: ['grounds'],
  GROUND: (id) => ['ground', id],
  GROUND_SUMMARY: ['ground-summary'],
  GROUND_REPORTS: ['ground-reports'],
  GROUND_COURTS: (id) => ['ground-courts', id],
  GROUND_AVAILABILITY: (id) => ['ground-availability', id],
  REPORTS: ['reports'],
  MEDIA: ['media'],
  MEDIA_ITEM: (id) => ['media-item', id],
  MEDIA_RECENT: ['media-recent'],
  MATCH_GALLERY: (matchId) => ['match-gallery', matchId],
  GALLERY_STATS: ['gallery-stats'],
  ALBUMS: ['albums'],
  ALBUM: (id) => ['album', id],
  NOTIFICATIONS: ['notifications'],
  NOTIFICATION_UNREAD_COUNT: ['notifications', 'unread-count'],
  USERS: ['users'],
  USER: (id) => ['user', id],
  ROLES: ['roles'],
  USER_DASHBOARD_STATS: ['user-dashboard-stats'],
  LOGIN_HISTORY: ['login-history'],
  SESSIONS: ['sessions'],
  SECURITY_INFO: ['security-info'],
  PUBLIC: {
    all: ['public'],
    home: () => ['public', 'home'],
    stats: () => ['public', 'stats'],
    search: (query) => ['public', 'search', query],
    tournaments: (params) => ['public', 'tournaments', params],
    matches: (params) => ['public', 'matches', params],
    match: (id) => ['public', 'match', id],
    teams: (params) => ['public', 'teams', params],
    players: (params) => ['public', 'players', params],
    player: (id) => ['public', 'player', id],
    team: (id) => ['public', 'team', id],
    grounds: (params) => ['public', 'grounds', params],
    ground: (id) => ['public', 'ground', id],
    gallery: (params) => ['public', 'gallery', params],
    albums: (params) => ['public', 'albums', params],
    album: (id) => ['public', 'album', id],
    sponsors: (params) => ['public', 'sponsors', params],
  },
};

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMIT_OPTIONS: [10, 25, 50, 100],
};
